import type { AttemptEndRequest, RewardProvider, RewardType } from '@arrows/shared';
import { loadProgress, mergeServerProgress } from '../game/progress';
import { ProgressApi } from './api';
import { DevAuthClient, type AuthClient, type Fetcher } from './auth';
import { createAttemptId } from './id';
import { LiffAuthClient } from './liffAuth';
import { AttemptOutbox } from './outbox';

export interface AttemptMetrics {
  readonly levelNumber: number;
  readonly failCount: number;
  readonly reachedGameOver: boolean;
  readonly continuedCount: number;
  readonly hintUsedCount: number;
}

interface ActiveAttempt extends AttemptMetrics {
  readonly attemptId: string;
  readonly startedAt: string;
}

interface OnlineEvents {
  addEventListener(type: 'online', listener: () => void): void;
  removeEventListener(type: 'online', listener: () => void): void;
}

interface AttemptOutboxPort {
  enqueueAttempt(request: { attemptId: string; level: number; startedAt: string }): void;
  endAttempt(attemptId: string, end: AttemptEndRequest): void;
  markGameOver(attemptId: string): void;
  enqueueReward(request: {
    idempotencyKey: string;
    provider: RewardProvider;
    rewardType: RewardType;
    grantedAt: string;
  }): void;
  flush(): Promise<void>;
}

interface ProgressApiPort {
  getProgress(): ReturnType<ProgressApi['getProgress']>;
  seedProgress(highestClearedLevel: number): ReturnType<ProgressApi['seedProgress']>;
}

export interface ProgressSyncOptions {
  readonly api?: ProgressApiPort;
  readonly outbox?: AttemptOutboxPort;
  readonly createId?: () => string;
  readonly now?: () => Date;
  readonly onlineEvents?: OnlineEvents | null;
}

export class ProgressSyncService {
  private readonly api?: ProgressApiPort;
  private readonly outbox?: AttemptOutboxPort;
  private readonly createId: () => string;
  private readonly now: () => Date;
  private readonly onlineEvents: OnlineEvents | null;
  private activeAttempt: ActiveAttempt | null = null;
  private listeningOnline = false;

  constructor(options: ProgressSyncOptions = {}) {
    this.api = options.api;
    this.outbox = options.outbox;
    this.createId = options.createId ?? createAttemptId;
    this.now = options.now ?? (() => new Date());
    this.onlineEvents = options.onlineEvents
      ?? (typeof window === 'undefined' ? null : window);
  }

  get currentAttemptId(): string | null {
    return this.activeAttempt?.attemptId ?? null;
  }

  async initialize(): Promise<void> {
    if (this.api === undefined || this.outbox === undefined) return;
    if (this.onlineEvents !== null && !this.listeningOnline) {
      this.onlineEvents.addEventListener('online', this.handleOnline);
      this.listeningOnline = true;
    }
    await this.outbox.flush();
    try {
      const server = await this.api.getProgress();
      mergeServerProgress(server);
      const seeded = await this.api.seedProgress(loadProgress().highestClearedLevel);
      if (seeded !== null) mergeServerProgress(seeded);
    } catch {
      // Sync is optional. Authentication, network, and server failures never block play.
    }
  }

  attemptBegan(levelNumber: number): string {
    if (this.activeAttempt !== null) {
      this.finishAttempt('restarted', null);
    }
    const startedAt = this.now().toISOString();
    const attemptId = this.createId();
    this.activeAttempt = {
      attemptId,
      startedAt,
      levelNumber,
      failCount: 0,
      reachedGameOver: false,
      continuedCount: 0,
      hintUsedCount: 0,
    };
    this.outbox?.enqueueAttempt({ attemptId, level: levelNumber, startedAt });
    this.flushSoon();
    return attemptId;
  }

  updateMetrics(metrics: AttemptMetrics): void {
    if (this.activeAttempt === null || this.activeAttempt.levelNumber !== metrics.levelNumber) return;
    this.activeAttempt = { ...this.activeAttempt, ...metrics };
    if (metrics.reachedGameOver) {
      this.outbox?.markGameOver(this.activeAttempt.attemptId);
      this.flushSoon();
    }
  }

  attemptCleared(levelNumber: number, elapsedMs: number): void {
    if (this.activeAttempt?.levelNumber !== levelNumber) return;
    this.finishAttempt('clear', Math.max(0, Math.round(elapsedMs)));
  }

  rewardGranted(provider: RewardProvider, rewardType: RewardType): void {
    if (this.outbox === undefined) return;
    this.outbox.enqueueReward({
      idempotencyKey: this.createId(),
      provider,
      rewardType,
      grantedAt: this.now().toISOString(),
    });
    this.flushSoon();
  }

  destroy(): void {
    if (this.onlineEvents !== null && this.listeningOnline) {
      this.onlineEvents.removeEventListener('online', this.handleOnline);
      this.listeningOnline = false;
    }
  }

  private finishAttempt(outcome: 'clear' | 'restarted', clearTimeMs: number | null): void {
    const attempt = this.activeAttempt;
    if (attempt === null) return;
    const end: AttemptEndRequest = {
      outcome,
      clearTimeMs,
      failCount: attempt.failCount,
      reachedGameOver: attempt.reachedGameOver,
      continuedCount: attempt.continuedCount,
      hintUsedCount: attempt.hintUsedCount,
      endedAt: this.now().toISOString(),
    };
    this.outbox?.endAttempt(attempt.attemptId, end);
    this.activeAttempt = null;
    this.flushSoon();
  }

  private readonly handleOnline = (): void => {
    this.flushSoon();
  };

  private flushSoon(): void {
    if (this.outbox !== undefined) void this.outbox.flush();
  }
}

export interface ConfiguredProgressSyncOptions {
  readonly apiBase?: string;
  readonly liffId?: string;
  readonly devSecret?: string;
  readonly development: boolean;
  readonly fetcher?: Fetcher;
  readonly storage?: Storage | null;
  readonly authClient?: AuthClient;
}

/** 末尾スラッシュを落とし、未設定（同期が無効な環境）を null で表す。 */
export function normalizeApiBase(apiBase: string | undefined): string | null {
  const trimmed = apiBase?.replace(/\/$/, '');
  return trimmed === undefined || trimmed.length === 0 ? null : trimmed;
}

export function createConfiguredAuthClient(
  apiBase: string,
  options: Pick<ConfiguredProgressSyncOptions,
  'liffId' | 'devSecret' | 'development' | 'fetcher'>,
): AuthClient | undefined {
  if (options.liffId !== undefined && options.liffId.length > 0) {
    return new LiffAuthClient(apiBase, options.liffId, options.fetcher);
  }
  if (options.development && options.devSecret !== undefined && options.devSecret.length > 0) {
    return new DevAuthClient(apiBase, options.devSecret, options.fetcher);
  }
  return undefined;
}

export function createConfiguredProgressSync(
  options: ConfiguredProgressSyncOptions,
): ProgressSyncService {
  const apiBase = normalizeApiBase(options.apiBase);
  if (apiBase === null) return new ProgressSyncService();
  const auth = options.authClient ?? createConfiguredAuthClient(apiBase, options);
  if (auth === undefined) return new ProgressSyncService();
  const api = new ProgressApi(apiBase, auth, options.fetcher);
  const outbox = new AttemptOutbox(api, options.storage);
  if (!outbox.enabled) return new ProgressSyncService();
  return new ProgressSyncService({ api, outbox });
}
